import React, { useEffect } from 'react';
import { withLanguage } from '../i18n/LanguageContext';
import { lowerCase } from 'lodash-es/string';
import { range } from 'lodash-es';
import styled, { ThemeProvider } from 'styled-components';
import responsive from '../../utils/responsive';
import { dark } from '../../theme';
import { PlainLink } from '../NavLink';
import Input from '../../forms/Input';

const inputHeight = '3rem';
const inputHeightXs = '2rem';

const Select = styled.select`
  width: 100%;
  height: ${inputHeight};
  background: white;
  color: black;
  padding-left: 5px;
  font-size: 1.1rem;
  border-radius: 0.5rem;
  border: none;
  option {
    color: black;
    background: white;
    display: flex;
    white-space: pre;
    min-height: 0.8rem;
    padding: 0 2px 1px;
  }

  ${responsive.xs.andSmaller`
    font-size: ${props => props.theme.textSize.xs};
    height: ${inputHeightXs};
  `}
`;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  & > :not(:first-child) {
    margin-left: 0.8em;
    ${responsive.xs.andSmaller`
      margin-left: 0.2em;
    `}
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Button = styled.button`
  height: 100%;
  background: ${props => props.theme.button.primary.backgroundColor.lighten(0.2).toHex()};
  color: ${props => props.theme.button.primary.color.toHex()};
  font-size: ${props => props.theme.textSize.l};
  border: none;
  border-radius: 0.5rem;
  ${responsive.sm.andSmaller`
    font-size: ${props => props.theme.textSize.m};
  `}
  ${responsive.xs.andSmaller`
    font-size: ${props => props.theme.textSize.xs};
  `}

  :hover {
    opacity: 0.9;
  }

  :active {
    opacity: 0.9;
  }

  :disabled {
    opacity: 0.8;
  }

  transition: all 0.2s ease;
  margin-left: -${props => props.theme.border.width};
`;

const Label = styled.label`
  font-size: ${props => props.theme.textSize.m};
  color: ${props => props.theme.input.background.toHex()};
  margin-bottom: 0.6em;
  font-weight: ${props => props.theme.fontWeight.bold};
  ${responsive.xs.andSmaller`
    font-size: ${props => props.theme.textSize.xs};
  `}
`;

const Container = styled.div`
  z-index: 999;
  position: relative;
  padding: 0.5em 1em;
  margin-bottom: 1em;
  background-color: #517e8e8f;
  width: 45%;
  ${responsive.lg.andSmaller`
    width: 70%;
  `}
  ${responsive.sm.andSmaller`
    width: 100%;
  `}
  ${responsive.xs.andSmaller`
    padding: 0.2em 0.5em;
  `}
`;

const HtmlInput = styled(Input)`
  height: ${inputHeight};
  width: 100%;
  border-radius: 0.5rem;
  ${responsive.xs.andSmaller`
    font-size: ${props => props.theme.textSize.xs};
    height: ${inputHeightXs};
  `}
`;

const Option = ({ value }) => <option value={value}>{value}</option>;

const RangeSelect = ({ from, to, ...rest }) => (
  <Select {...rest}>
    {range(from, to).map(value => (
      <Option key={value} value={value} />
    ))}
  </Select>
);

const BookingForm = ({ content: { booking }, language }) => {
  const currentYear = new Date().getFullYear();
  const lan = lowerCase(language);
  const phobs_lang = lan === 'mn' ? 'me' : lan;
  useEffect(() => {
    try {
      /*global phobsSetupForm*/
      phobsSetupForm(phobs_lang);
    } catch (e) {
      console.warn(e);
    }
  }, [phobs_lang]);

  return (
    <ThemeProvider theme={dark}>
      <Container>
        <form action="https://secure.phobs.net/book.php" method="get" id="phobs_book" name="phobs_book">
          <Horizontal>
            <Field style={{ flexGrow: 10 }}>
              <Label>{booking.arrival}</Label>
              <Horizontal>
                <RangeSelect from={1} to={32} name="check_in_day" size="1" />
                <RangeSelect from={1} to={13} name="check_in_month" size="1" />
                <RangeSelect from={currentYear} to={currentYear + 1} name="check_in_year" size="1" />
              </Horizontal>
            </Field>
            <Field>
              <Label>{booking.nights}</Label>
              <RangeSelect from={1} to={32} name="nights" size="1" />
            </Field>
            <Field style={{ flexGrow: 1, maxWidth: '25%' }}>
              <Label>{booking.code}</Label>
              <HtmlInput name="partners_access" type="text" maxLength="20" placeholder={booking.optional} />
            </Field>
            <Field style={{ flexGrow: 4 }}>
              <Label>&nbsp;</Label>
              <Button name="book" type="submit">
                {booking.check}
              </Button>
            </Field>
          </Horizontal>
          <PlainLink
            href="https://secure.phobs.net/book.php?company_id=0d835b63d6171229e38a60cd4cd45796&amp;lang=en&amp;view_cancel=1"
            target="_blank"
            style={{ lineHeight: '2rem' }}
          >
            {booking.cancel}
          </PlainLink>
          <input name="hotel" type="hidden" value="17a1020fd45fdfb050bf8de448bca331" />
          <input name="company_id" type="hidden" value="0d835b63d6171229e38a60cd4cd45796" />
          <input name="view_cancel" type="hidden" value="" />
          <input name="lang" type="hidden" value={phobs_lang} />
          <input name="currency" type="hidden" value="EUR" />
        </form>
      </Container>
    </ThemeProvider>
  );
};

export default withLanguage(BookingForm);
